import { DependencyList, useEffect, useState } from 'react';
import { SelectOption } from '../../../../lib/components';

interface BuildOptionsArgs<T> {
  map?: (item: T) => SelectOption;
  filter?: (item: T) => boolean;
  isItemHidden?: (item: T) => boolean;
  getItems: GetSync<T> | GetAsync<T>;
  append?: SelectOption[];
  prepend?: SelectOption[];
}

const defaultMap = <T,>(item: T): SelectOption => {
  return { text: String(item), value: String(item) };
};

const buildOptions = <T,>(items: T[] | undefined, args: BuildOptionsArgs<T>) => {
  const map: (e: T) => SelectOption = (e) => {
    const option = args.map ? args.map(e) : defaultMap(e);
    if (args.isItemHidden) option.hidden = args.isItemHidden(e);
    return option;
  };
  let options = items ? items.filter((e) => (!args.filter ? true : args.filter(e))).map(map) : [];
  if (args.append) {
    options = options.concat(args.append);
  }
  if (args.prepend) {
    options = args.prepend.concat(options);
  }
  return options;
};

interface GetSync<T> {
  sync: () => T[];
}

interface GetAsync<T> {
  async: () => Promise<T[]>;
}

export interface UseOptionsArgs<T> extends Omit<BuildOptionsArgs<T>, 'getItems'> {
  deps?: DependencyList;
}

export const useOptions = <T,>(args: BuildOptionsArgs<T>, deps?: DependencyList) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  useEffect(() => {
    const abortController = new AbortController();
    const getItems = async () => {
      try {
        const getSync = (args.getItems as GetSync<T>).sync;
        const isSync = Object.keys(args.getItems).includes('sync');
        if (isSync) {
          setOptions(buildOptions(getSync(), args));
        } else {
          const getAsync = (args.getItems as GetAsync<T>).async;
          setOptions(buildOptions(await getAsync(), args));
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error(error);
        }
      }
    };

    getItems();
    return () => abortController.abort();
  }, deps ?? []);
  return options;
};
